export function* treeIterator<TNode>(root: TNode | TNode[], getChild: (node: TNode) => TNode[]): Generator<TNode> {
    for (const rootElement of _.isArray(root) ? root : [root]) {
        yield rootElement;
        for (const child of treeIterator(getChild(rootElement), getChild)) {
            yield child;
        }
    }
}

export function treeToArray<TNode extends { children: TNode[] }>(root: TNode | TNode[]): TNode[];
export function treeToArray<TNode>(root: TNode | TNode[], getChild: (node: TNode) => TNode[]): TNode[];
export function treeToArray<TNode extends { children?: TNode[] }>(root: TNode | TNode[], getChild?: (node: TNode) => TNode[]): TNode[] {
    getChild = _.defaultTo(getChild, node => node.children || []);
    return [...treeIterator(root, getChild)];
}

type IterableType<T> = IterableIterator<T> | readonly T[];

export function getAncestorsAndSelf<TNode extends { parent: TNode }>(node: TNode): Generator<TNode>;
export function getAncestorsAndSelf<TNode>(node: TNode, getParent: (node: TNode) => TNode): Generator<TNode>;
export function* getAncestorsAndSelf<TNode extends { parent: TNode }>(node: TNode, getParent?: (node: TNode) => TNode): Generator<TNode> {
    getParent = _.defaultTo(getParent, node => node.parent);
    const parent = getParent(node);
    if (parent) {
        for (const ancestor of getAncestorsAndSelf(parent, getParent)) {
            yield ancestor;
        }
    }
    yield node;
}

export function findById<T extends { id?: K }, K>(collection: IterableType<T>, id: K, defaultValue: T): T;
export function findById<T extends { id?: K }, K>(collection: IterableType<T>, id: K): T | undefined;
export function findById<T extends { id?: K }, K>(collection: IterableType<T>, id: K, defaultValue?: T): T | undefined {
    if (defaultValue === undefined) {
        return findBy(collection, id);
    }
    return findBy(collection, id, defaultValue);
}

export function findBy<T extends { id?: K }, K>(collection: IterableType<T>, id: K): T | undefined;
export function findBy<T extends { id?: K }, K>(collection: IterableType<T>, id: K, defaultValue: T): T;
export function findBy<T>(collection: IterableType<T>, search: (c: T) => boolean): T | undefined;
export function findBy<T>(collection: IterableType<T>, search: (c: T) => boolean, defaultValue: Partial<T>): T;
export function findBy<T>(collection: IterableType<T>, search: (c: T) => boolean, defaultValue?: Partial<T>): T {
    const innerSearch = _.isFunction(search) ? search : (c: any) => c.id === search;
    for (const c of collection || []) {
        if (innerSearch(c)) {
            return c;
        }
    }
    return defaultValue as T;
}

export function toMap<T extends { id: unknown }>(collection: IterableType<T>): Map<T extends { id: infer TK } ? TK : never, T>;
export function toMap<T, TKey>(collection: IterableType<T>, toPair: (c: T, index: number) => [TKey]): Map<TKey, T>;
export function toMap<T, TKey, TValue>(collection: IterableType<T>, toPair: (c: T, index: number) => [TKey, TValue]): Map<TKey, TValue>;
export function toMap<T, TKey, TValue>(collection: IterableType<T>, toPair?: (c: T, index: number) => [TKey, TValue] | [TKey]): Map<TKey, TValue> {
    return toMapImpl(collection, toPair);
}

function toMapImpl<T, TKey, TValue>(collection: IterableType<T>, toPair?: (c: T, index: number) => [TKey, TValue] | [TKey]): Map<TKey, TValue> {
    const pairing = toPair ?? ((a: T & { id?: TKey }) => [a.id, a]);
    const mapper = (a: T, i: number) => {
        const pair = pairing(a, i);
        if (_.size(pair) === 1) {
            pair[1] = a;
        }
        return pair as [TKey, TValue];
    };
    return new Map<TKey, TValue>(transform(collection, mapper));
}

export function toMultiMap<T extends { id: unknown }>(collection: IterableType<T>): T extends { id: infer TKey } ? Map<TKey, T[]> : never;
export function toMultiMap<T, TKey>(collection: IterableType<T>, getKey: (c: T) => TKey): Map<TKey, T[]>;
export function toMultiMap<T, TKey>(collection: IterableType<T>, getKey?: (c: T) => TKey): Map<TKey, T[]> {
    getKey = getKey || ((a: any) => a.id);
    const res = new Map<TKey, T[]>();
    for (const c of collection || []) {
        const key = getKey(c);
        if (res.has(key)) {
            res.get(key)!.push(c);
        } else {
            res.set(key, [c]);
        }
    }
    return res;
}

export function* transform<T, V>(collection: IterableType<T>, fn: (c: T, index: number) => V): IterableIterator<V> {
    let index = 0;
    for (const c of collection || []) {
        yield fn(c, index++);
    }
}

export function createIndex<T extends { id: TKey }, TKey>(collection: IterableType<T>, defaultValue?: Partial<T>): (id: TKey) => T;
export function createIndex<T, TKey>(
    collection: IterableType<T>,
    toPair: (c: T, index: number) => [TKey],
    defaultValue?: Partial<T>
): (id: TKey) => T;
export function createIndex<T, TKey, TValue>(
    collection: IterableType<T>,
    toPair: (c: T, index: number) => [TKey, TValue],
    defaultValue?: Partial<TValue>
): (id: TKey) => TValue;
export function createIndex<T, TKey, TValue>(
    collection: IterableType<T>,
    toPair?: ((c: T, index: number) => [TKey, TValue] | [TKey]) | TValue,
    defaultValue?: TValue
): (id: TKey) => TValue {
    if (Function.isFunction(toPair)) {
        const index = toMapImpl(collection, toPair);
        return id => index.get(id) ?? (defaultValue as TValue);
    } else {
        const index = toMapImpl(collection);
        return id => (index.get(id) as TValue) ?? (toPair as TValue);
    }
}
